/**
 * 상품-카테고리 링크 벌크 업로드 (비동기 잡) 상수
 *
 * 백엔드 스펙: amondz-backend-monorepo#4834
 */

/**
 * 엔드포인트.
 * 스펙 문서(§5)는 `/api/relationships/product-category` 로 적혀 있지만, 실제 컨트롤러는
 * files.module 의 RouterModule 로 `api/files/excel-upload` 아래 중첩 등록돼 있다.
 * 신규 GET 2개의 prefix 는 아직 백엔드 확정 전이므로 여기 한 곳만 고치면 되도록 모아둔다.
 */
const PRODUCT_CATEGORY_LINK_BULK_BASE_PATH = '/files/excel-upload/relationships/product-category';

export const PRODUCT_CATEGORY_LINK_BULK_API_PATH = {
  UPLOAD: PRODUCT_CATEGORY_LINK_BULK_BASE_PATH,
  JOB: (jobId: string): string => `${PRODUCT_CATEGORY_LINK_BULK_BASE_PATH}/jobs/${jobId}`,
  FAILED_CSV: (jobId: string): string => `${PRODUCT_CATEGORY_LINK_BULK_BASE_PATH}/jobs/${jobId}/failed-csv`,
} as const;

/**
 * 워커가 CSV 첫 행을 이 문자열과 **완전 일치**로 검증한다 (컬럼명·순서 모두).
 * 불일치 시 잡이 즉시 실패하므로, 샘플 CSV 도 S3 정적 파일이 아니라 이 값으로 생성한다.
 */
export const PRODUCT_CATEGORY_LINK_CSV_HEADER = '상품 ID,상품명,1차 카테고리 ID,2차 카테고리 ID';

/** 샘플 CSV 예시 행 — 2차 카테고리 ID 는 빈 칸 허용 */
export const PRODUCT_CATEGORY_LINK_CSV_SAMPLE_ROWS = [
  '1234,아이폰 케이스,10,15',
  '1235,에어팟 케이스,10,15',
  '1236,스마트워치 밴드,20,',
];

export const PRODUCT_CATEGORY_LINK_CSV_SAMPLE_FILE_NAME = 'product-category-link-sample.csv';

export const PRODUCT_CATEGORY_LINK_BULK_CSV_CONTENT_TYPE = 'text/csv';

/** 백엔드 검증과 일치 (스펙 §7.1) */
export const PRODUCT_CATEGORY_LINK_BULK_MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024;
export const PRODUCT_CATEGORY_LINK_BULK_MAX_ROWS = 200000;

/** 안내 문구·에러 메시지에 함께 쓰는 용량 표기 — 한도가 바뀌면 위 바이트 값과 함께 여기만 고친다 */
export const PRODUCT_CATEGORY_LINK_BULK_MAX_FILE_SIZE_LABEL = '30MB';

/**
 * 업로드 요청 타임아웃.
 * SuperFetchV2 기본값(120초)은 30MB 전송에 부족할 수 있다.
 */
export const PRODUCT_CATEGORY_LINK_BULK_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

/** localStorage 키 — 어드민 계정별로 분리한다 (공용 PC 에서 남의 jobId 를 물려받지 않도록) */
export const PRODUCT_CATEGORY_LINK_BULK_JOB_STORAGE_KEY_PREFIX = '__amondz_admin_pcl_bulk_job__';

/**
 * 적응형 폴링 간격.
 * 20만 행은 5분 가까이 걸리지만 100행짜리는 수 초에 끝나므로, 초반에는 촘촘히 보고
 * 시간이 지나면 간격을 늘린다. 경과 시간이 구간의 untilMs 미만이면 그 간격을 쓴다.
 */
export const PRODUCT_CATEGORY_LINK_BULK_POLL_STEPS = [
  { untilMs: 30 * 1000, intervalMs: 2 * 1000 },
  { untilMs: 2 * 60 * 1000, intervalMs: 5 * 1000 },
] as const;

/** 마지막 구간을 넘어선 뒤의 간격 */
export const PRODUCT_CATEGORY_LINK_BULK_POLL_FALLBACK_INTERVAL_MS = 15 * 1000;

/** 404 를 만료로 확정하기 전 재확인 대기 — 일시적 404 로 진행 추적을 잃지 않기 위함 */
export const PRODUCT_CATEGORY_LINK_BULK_EXPIRED_RECHECK_DELAY_MS = 1000;

/** 창 포커스 복귀가 잦을 때 요청이 폭증하지 않도록 짧게 dedupe */
export const PRODUCT_CATEGORY_LINK_BULK_JOB_STALE_TIME_MS = 1000;

/**
 * 이 기능에서 다루는 엑셀 업로드 에러 코드 (백엔드 #4914 확정 기준).
 *
 * 코드 배정 주의 — 기존 공용 코드와 신규 코드가 갈린다:
 * - 005(20MB)·006(xlsx만)은 brand·product 엑셀이 쓰는 공용 코드로, 이 기능과 무관하다
 * - 이 기능의 mime 불일치는 010, 30MB 초과는 011 (신설)
 * - 001·002·007·008·009 는 워커 fast-fail 코드로, HTTP 가 아니라 failedReason 으로 온다
 *
 * 전역 `constants/errorResponse/category/excelUpload.js` 를 쓰지 않는 이유: 008~011 이 없고
 * 키 표기(EXCELUPLOAD_005)도 다르다.
 */
export const PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE = {
  /** 데이터 0행 (워커 failedReason) */
  EMPTY_FILE: 'EXCELUPLOAD001',
  /** 파일 내용을 파싱할 수 없음 (워커 failedReason) */
  INVALID_SHEET: 'EXCELUPLOAD002',
  /** 최대 행 수 초과 (워커 failedReason) */
  ROWS_EXCEEDED: 'EXCELUPLOAD007',
  /** 헤더 컬럼명·순서 불일치 (워커 failedReason) */
  INVALID_HEADER: 'EXCELUPLOAD008',
  /** UTF-8 아님 (워커 failedReason) */
  INVALID_ENCODING: 'EXCELUPLOAD009',
  /** mime 불일치 — CSV 아님 (400) */
  NOT_CSV: 'EXCELUPLOAD010',
  /** 파일 크기 30MB 초과 (400) */
  FILE_SIZE_EXCEEDED: 'EXCELUPLOAD011',
} as const;

/**
 * 에러 코드 → 한글 메시지.
 *
 * 주 용도는 **워커 failedReason 번역**이다. 워커는 코드 문자열만 던지므로
 * (`throw new Error('EXCELUPLOAD007')`) 이 매핑이 유일한 한글 지점이고, 그래서
 * 백엔드 msg.ts 문구보다 행동 지침이 구체적인 문구를 쓴다 (예: 008 에 정확한 헤더 명시).
 *
 * HTTP 4xx 는 서버 필터가 한글 메시지를 완비하므로 서버 메시지를 우선하고
 * (resolveBulkUploadHttpErrorMessage), 이 매핑은 폴백으로만 쓰인다.
 * 010·011 이 여기 있는 이유: 클라이언트 사전 차단(011)과 그 폴백용.
 */
export const PRODUCT_CATEGORY_LINK_BULK_ERROR_MESSAGE: Record<string, string> = {
  [PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE.EMPTY_FILE]: '업로드한 CSV 에 데이터가 없습니다.',
  [PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE.INVALID_SHEET]:
    '파일 내용을 읽을 수 없습니다. 다시 저장한 뒤 업로드해 주세요.',
  [PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE.ROWS_EXCEEDED]: `한 번에 최대 ${PRODUCT_CATEGORY_LINK_BULK_MAX_ROWS.toLocaleString()}행까지 업로드 가능합니다.`,
  [PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE.INVALID_HEADER]: `CSV 헤더 형식이 올바르지 않습니다. 첫 행이 "${PRODUCT_CATEGORY_LINK_CSV_HEADER}" 와 정확히 일치해야 합니다.`,
  [PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE.INVALID_ENCODING]:
    'CSV 파일이 UTF-8 인코딩이 아닙니다. 엑셀에서 "다른 이름으로 저장 → CSV UTF-8" 로 저장해 주세요.',
  [PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE.NOT_CSV]:
    'CSV 파일만 업로드 가능합니다. 엑셀에서 "다른 이름으로 저장 → CSV UTF-8" 로 저장해 주세요.',
  [PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE.FILE_SIZE_EXCEEDED]: `파일 용량은 최대 ${PRODUCT_CATEGORY_LINK_BULK_MAX_FILE_SIZE_LABEL} 까지 업로드 가능합니다.`,
};

/** 알 수 없는 코드로 실패했을 때 */
export const PRODUCT_CATEGORY_LINK_BULK_DEFAULT_ERROR_MESSAGE = '업로드 처리 중 오류가 발생했습니다.';

/** 안내 문구 (스펙 §9.3) */
export const PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT = {
  /** 처리 중 */
  IN_PROGRESS: '페이지를 떠나도 처리는 계속됩니다. 페이지에 다시 들어오면 진행 상황이 자동으로 복원됩니다.',
  /** 대기 중 — 큐 동시성이 1이라 다른 어드민의 업로드가 돌고 있으면 순번을 기다린다 */
  WAITING: '다른 업로드가 처리 중이면 순차적으로 진행됩니다.',
  /** stalled(stuck) 판정 — 워커가 재개하거나 실패로 넘어갈 때까지 잡은 살아 있다 */
  STUCK: '처리가 지연되고 있습니다. 잠시 후 자동으로 재개됩니다.',
  /** 완료 — esSync 집계가 없을 때의 폴백 문구 */
  COMPLETED: 'DB 반영 완료. 검색 인덱스 동기화는 백그라운드로 몇 분 더 소요됩니다.',
  /** 완료 + ES 동기화까지 종료 */
  ES_SYNC_COMPLETED: '검색 인덱스 반영이 완료되었습니다.',
  /** ES 동기화 진행 중 — 카드가 수치와 함께 표시 */
  ES_SYNC_IN_PROGRESS: '페이지를 닫아도 반영은 계속됩니다.',
  /** 실패 */
  FAILED: '운영팀에 문의해주세요.',
  /** 중복 요청(409) */
  DUPLICATED: '이미 진행 중인 업로드가 있습니다. 아래에서 진행 상황을 확인하세요.',
  /** 잡 만료(404) */
  EXPIRED: '이전 잡 정보가 만료되었습니다.',
} as const;
